/**
 * Auditoria -- antes solo existia GET /audit/verify (un veredicto de
 * integridad de la cadena de hash, sin forma de VER los eventos). Ahora
 * tambien lista los eventos via GET /audit (src/audit.ts). Institucional
 * solo, igual que catalogos/usuarios.
 *
 * El alcance de que se audita hoy esta documentado en el propio backend
 * (cabecera de src/audit.ts): creacion/transicion de estado de actividades,
 * transicion de estado de unidad, observaciones de revision, y toda la
 * consola de usuarios/catalogos. NO cubre login/logout ni ediciones
 * campo-a-campo del asistente -- ver esa nota si "no aparece algo que
 * esperaba".
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchAuditEntries, fetchAuditVerify, type AuditEntry } from '../lib/queries';
import { PageHeader, QueryState } from '../components/ui';

const ACTION_LABEL: Record<string, string> = {
  create: 'Creación',
  update: 'Edición',
  delete: 'Eliminación',
  state_change: 'Cambio de estado (actividad)',
  unit_state_change: 'Cambio de estado (unidad)',
  unit_state_auto: 'Cambio de estado automático (unidad)',
  review: 'Decisión de revisión',
  permission_grant: 'Permiso otorgado',
  permission_revoke: 'Permiso retirado',
  config_change: 'Cambio de configuración',
  import: 'Importación',
};

const ENTITY_LABEL: Record<string, string> = {
  processing_activity: 'Actividad',
  organizational_unit: 'Unidad',
  review_observation: 'Observación de revisión',
  user: 'Usuario',
  import_batch: 'Lote de importación',
  catalog_item: 'Ítem de catálogo',
};

const ACTIONS = Object.keys(ACTION_LABEL);
const ENTITY_TYPES = Object.keys(ENTITY_LABEL);
const PAGE_LIMIT = 50;

export function Audit() {
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [pages, setPages] = useState<AuditEntry[][]>([]);

  const verifyQ = useQuery({ queryKey: ['audit', 'verify'], queryFn: fetchAuditVerify });

  const firstPageQ = useQuery({
    queryKey: ['audit', 'entries', action, entityType],
    queryFn: () => fetchAuditEntries({ limit: PAGE_LIMIT, action: action || undefined, entityType: entityType || undefined }),
  });

  const entries = [...(firstPageQ.data ?? []), ...pages.flat()];
  const lastId = entries.length > 0 ? entries[entries.length - 1]!.id : undefined;
  // La ultima pagina cargada (o la primera, si aun no se pidio "cargar mas")
  // trajo menos que el limite -> no hay pagina siguiente.
  const lastPageSize = pages.length > 0 ? pages[pages.length - 1]!.length : (firstPageQ.data?.length ?? 0);
  const hasMore = firstPageQ.isSuccess && lastPageSize === PAGE_LIMIT;
  const [loadingMore, setLoadingMore] = useState(false);

  async function loadMore() {
    if (!lastId) return;
    setLoadingMore(true);
    try {
      const next = await fetchAuditEntries({ limit: PAGE_LIMIT, action: action || undefined, entityType: entityType || undefined, beforeId: lastId });
      setPages((p) => [...p, next]);
    } finally {
      setLoadingMore(false);
    }
  }

  function resetFilters(patch: { action?: string; entityType?: string }) {
    setPages([]);
    if (patch.action !== undefined) setAction(patch.action);
    if (patch.entityType !== undefined) setEntityType(patch.entityType);
  }

  return (
    <>
      <PageHeader
        eyebrow="Configuración institucional"
        title="Auditoría"
        sub="Bitácora append-only de acciones administrativas y de cumplimiento, con cadena de hash verificable."
      />

      <IntegrityCard
        isLoading={verifyQ.isLoading}
        error={verifyQ.error}
        result={verifyQ.data}
        onRefresh={() => void verifyQ.refetch()}
      />

      <div style={{ display: 'flex', gap: 8, margin: '14px 0', flexWrap: 'wrap' }}>
        <select value={action} onChange={(e) => resetFilters({ action: e.target.value })}>
          <option value="">Todas las acciones</option>
          {ACTIONS.map((a) => (
            <option key={a} value={a}>
              {ACTION_LABEL[a]}
            </option>
          ))}
        </select>
        <select value={entityType} onChange={(e) => resetFilters({ entityType: e.target.value })}>
          <option value="">Todas las entidades</option>
          {ENTITY_TYPES.map((t) => (
            <option key={t} value={t}>
              {ENTITY_LABEL[t]}
            </option>
          ))}
        </select>
      </div>

      <QueryState isLoading={firstPageQ.isLoading} error={firstPageQ.error}>
        <div className="tablewrap">
          <table className="data">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Actor</th>
                <th>Acción</th>
                <th>Entidad</th>
                <th>Cambio</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id}>
                  <td className="mono" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {e.occurred_at}
                  </td>
                  <td style={{ fontSize: 13 }}>{e.actor_email ?? <span className="muted">sistema</span>}</td>
                  <td>
                    <span className="pill">{ACTION_LABEL[e.action] ?? e.action}</span>
                  </td>
                  <td style={{ fontSize: 13 }}>
                    {ENTITY_LABEL[e.entity_type] ?? e.entity_type}
                    <div className="muted mono" style={{ fontSize: 11 }}>
                      {e.entity_id}
                    </div>
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {e.previous_state && <span className="muted">{e.previous_state} → </span>}
                    {e.new_state ?? <span className="muted">—</span>}
                  </td>
                </tr>
              ))}
              {entries.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted" style={{ padding: 20 }}>
                    Sin eventos para este filtro.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {hasMore && (
          <div style={{ marginTop: 10 }}>
            <button className="btn btn--ghost" disabled={loadingMore} onClick={() => void loadMore()}>
              {loadingMore ? 'Cargando…' : 'Cargar más'}
            </button>
          </div>
        )}
      </QueryState>
    </>
  );
}

function IntegrityCard({
  isLoading,
  error,
  result,
  onRefresh,
}: {
  isLoading: boolean;
  error: unknown;
  result: { ok: boolean; brokenAtId: number | null; rowsChecked: number } | undefined;
  onRefresh: () => void;
}) {
  return (
    <div className="card" style={{ padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <b style={{ fontSize: 14 }}>Integridad de la cadena</b>
        {isLoading && <span className="muted" style={{ fontSize: 12 }}>Verificando…</span>}
        {error ? <span className="pill pill--critical">Error al verificar</span> : null}
        {result && (
          <>
            <span className={`pill pill--${result.ok ? 'success' : 'critical'}`}>{result.ok ? 'Íntegra' : 'Manipulada'}</span>
            <span className="muted" style={{ fontSize: 12 }}>
              {result.rowsChecked} evento{result.rowsChecked === 1 ? '' : 's'} verificado{result.rowsChecked === 1 ? '' : 's'}
              {!result.ok && result.brokenAtId !== null && ` · cadena rota en el evento #${result.brokenAtId}`}
            </span>
          </>
        )}
        <span style={{ flex: 1 }} />
        <button className="btn btn--ghost" style={{ fontSize: 12, padding: '3px 8px' }} onClick={onRefresh} disabled={isLoading}>
          Volver a verificar
        </button>
      </div>
    </div>
  );
}
